// frontend/src/admin/blockforms/ImageCarouselForm.js
import React, { useState, useEffect } from 'react';
import { buildBlockSave } from './blockSaveTarget';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';

const ImageCarouselForm = ({ level, entityId, section, editingBlock, nextOrderIndex, onSaved, onCancel }) => {
    const existing = editingBlock?.content_data || {};

    const [images, setImages] = useState(existing.images || []);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (images.length === 0) return;

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [images]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await fetch(`${API_BASE}/rich-content/upload-image`, {
                method: 'POST',
                body: formData
            });
            if (!res.ok) throw new Error('Upload failed');
            const data = await res.json();
            setImages(prev => [...prev, { url: data.file_path, en: { caption: '' }, hi: { caption: '' } }]);
        } catch (err) {
            setError('Image upload failed');
        } finally {
            setUploading(false);
        }
    };

    const updateCaption = (index, lang, value) => {
        const updated = [...images];
        updated[index] = { ...updated[index], [lang]: { caption: value } };
        setImages(updated);
    };

    const removeImage = (index) => {
        setImages(images.filter((_, i) => i !== index));
    };

    const moveImage = (index, direction) => {
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= images.length) return;
        const updated = [...images];
        [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
        setImages(updated);
    };

    const handleSave = async () => {
        if (images.length === 0) {
            setError('Please add at least one image');
            return;
        }
        setError(null);
        setSaving(true);

        const content_data = { images };
        const { url, method, payload } = buildBlockSave({
            level, entityId, section, editingBlock,
            blockType: 'image_carousel',
            contentData: content_data,
            orderIndex: nextOrderIndex,
        });

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Save failed');
            }
            onSaved();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ace-form">
            <h4>{editingBlock ? 'Edit' : 'Add'} Image Carousel</h4>
            {error && <div className="ace-error">{error}</div>}

            <label>Add Image</label>
            <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} />

            {images.map((img, index) => (
                <div key={index} className="ace-table-editor">
                    <p className="ace-image-preview-path">{img.url}</p>
                    <label>Caption (English)</label>
                    <input
                        type="text"
                        value={img.en?.caption || ''}
                        onChange={(e) => updateCaption(index, 'en', e.target.value)}
                    />
                    <label>Caption (Hindi, optional)</label>
                    <input
                        type="text"
                        value={img.hi?.caption || ''}
                        onChange={(e) => updateCaption(index, 'hi', e.target.value)}
                    />
                    <div className="ace-block-actions">
                        <button type="button" onClick={() => moveImage(index, 'up')} disabled={index === 0}>↑</button>
                        <button type="button" onClick={() => moveImage(index, 'down')} disabled={index === images.length - 1}>↓</button>
                        <button type="button" onClick={() => removeImage(index)}>🗑️ Remove</button>
                    </div>
                </div>
            ))}

            <div className="ace-form-actions">
                <button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                <button onClick={onCancel} className="ace-cancel-btn">Cancel</button>
            </div>
        </div>
    );
};

export default ImageCarouselForm;